import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import type { EChartsType } from 'echarts';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../lib/api';

export function AttendanceChart({ theme }: { theme: 'light' | 'dark' }): JSX.Element {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<EChartsType | null>(null);
  const { data: attendanceResponse, isLoading } = useQuery({
    queryKey: ['attendance'],
    queryFn: () =>
      apiFetch<{ items: Array<{ date: string; players: number }> }>('/attendance/'),
  });
  const attendanceData =
    attendanceResponse?.items.map((item) => ({ label: item.date, value: item.players })) ?? [];

  useEffect(() => {
    if (!chartRef.current) return;
    chartInstance.current?.dispose();
    const instance = echarts.init(chartRef.current);
    chartInstance.current = instance;

    const resizeHandler = () => instance.resize();
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      instance.dispose();
    };
  }, [theme]);

  useEffect(() => {
    if (!chartInstance.current) return;

    const styles = getComputedStyle(document.documentElement);
    const toHsl = (token: string) => `hsl(${styles.getPropertyValue(token).trim()})`;

    const axisLineColor = toHsl('--border');
    const textColor = toHsl('--muted-foreground');
    const accent = toHsl('--accent');
    const accentAlt = toHsl('--ring');
    const cardColor = toHsl('--card');

    const option = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: cardColor,
        borderColor: axisLineColor,
        textStyle: { color: toHsl('--foreground') },
      },
      grid: { left: 36, right: 16, bottom: 48, top: 16 },
      xAxis: {
        type: 'category',
        data: attendanceData.map((item) => item.label),
        inverse: true,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisLabel: { color: textColor, rotate: 45 },
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: axisLineColor } },
        splitLine: { lineStyle: { color: axisLineColor, type: 'dashed' } },
        axisLabel: { color: textColor },
        name: 'Кол-во игроков',
        nameTextStyle: { color: textColor },
      },
      series: [
        {
          name: 'Посещаемость',
          type: 'line',
          smooth: 0.3,
          symbol: 'circle',
          symbolSize: 6,
          data: attendanceData.map((item) => item.value),
          lineStyle: { width: 3, color: accentAlt },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(255,222,0,0.75)' },
              { offset: 1, color: 'rgba(255,222,0,0.08)' },
            ]),
          },
          itemStyle: { color: accentAlt },
          markPoint: {
            symbol: 'circle',
            symbolSize: 12,
            itemStyle: { color: accent },
            label: { show: false },
            data: [
              { type: 'max', name: 'max' },
              { type: 'min', name: 'min' },
            ],
          },
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [theme, attendanceData]);

  return (
    <div className="card p-4 space-y-4">
      <div>
        <p className="text-2xl font-semibold">Посещаемость</p>
        <p className="text-sm text-muted-foreground">Динамика посещаемости TSG игр</p>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative h-72"
      >
        <div className="h-full w-full" ref={chartRef} />
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-end gap-2">
              {[0, 1, 2, 3].map((index) => (
                <motion.div
                  key={index}
                  className="w-2 rounded-sm bg-primary/70"
                  animate={{ height: [12, 36, 16] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: index * 0.15 }}
                />
              ))}
            </div>
          </div>
        ) : null}
      </motion.div>
    </div>
  );
}
